'use client'

import { Sparkles } from 'lucide-react'
import Link from 'next/link'
import { CHARACTER_THEMES } from '@/lib/character-themes'
import {
  formatTemplate,
  type TrackerCopy,
  type TrackerLocale,
  type TrackerState,
} from '@/lib/santa-tracker'
import { TrackerCountdown } from './tracker-countdown'
import { formatNumber } from './format'

/**
 * Cabecera: el único `<h1>` de la página, la cuenta atrás grande y la frase de
 * estado. Todo cambia con la fase, así que el mismo bloque sirve en agosto y en
 * Nochebuena.
 *
 * El badge y el callout no llevan colores a mano: salen de los acentos de Santa
 * del registro de temas, los mismos que usan las guías sobre este rojo.
 */
const SANTA_ACCENT = CHARACTER_THEMES.santa.accent

/** Enlace a la guía de la carta, resuelto en servidor solo para el locale que lo tiene. */
export interface TrackerHeroLetterLink {
  href: string
  label: string
}

export function TrackerHero({
  copy,
  locale,
  state,
  statusMessage,
  daysToChristmas,
  arriving,
  letterLink,
}: {
  copy: TrackerCopy
  locale: TrackerLocale
  state: TrackerState
  statusMessage: string
  daysToChristmas: number
  arriving: boolean
  letterLink?: TrackerHeroLetterLink
}) {
  return (
    <header className="flex flex-col items-center gap-5 text-center">
      {/* Mismo idioma visual que el badge del hero de la home: pastilla
          translúcida y chispa ámbar. Un `bg-red-500/20` sobre este rojo no se
          distingue del fondo. */}
      <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white backdrop-blur-sm">
        <Sparkles className="h-4 w-4 text-amber-200" aria-hidden />
        {copy.hero.badge[state.phase]}
      </span>
      <h1 className="font-christmas text-4xl font-bold leading-tight md:text-6xl">
        {copy.hero.h1[state.phase]}
      </h1>
      <p className="max-w-3xl text-lg text-white/85 md:text-xl">{copy.hero.intro[state.phase]}</p>

      <TrackerCountdown
        countdown={copy.countdown}
        locale={locale}
        heading={copy.countdown.heading[state.phase]}
        remainingMs={state.countdownMs}
        arriving={arriving}
      />

      {letterLink && (
        <Link
          href={letterLink.href}
          className="font-christmas whitespace-nowrap rounded-full border border-white/40 bg-white/10 px-5 py-2 text-base font-semibold text-white transition hover:bg-white/20"
        >
          {letterLink.label}
        </Link>
      )}

      <p className="text-sm text-white/70">
        {daysToChristmas > 0
          ? formatTemplate(copy.countdown.christmasNote, {
              days: formatNumber(daysToChristmas, locale),
            })
          : copy.countdown.christmasToday}
      </p>

      <p
        aria-live="polite"
        className={`max-w-2xl rounded-2xl border px-5 py-3 text-base ${SANTA_ACCENT.note}`}
      >
        {statusMessage}
      </p>

      <a
        href="#tracker-map"
        className="font-christmas whitespace-nowrap rounded-full bg-secondary px-7 py-3 text-lg font-semibold text-secondary-foreground transition-transform hover:scale-105"
      >
        {copy.hero.toMap}
      </a>
    </header>
  )
}
